import { useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useScrollSpy } from '@/hooks/useScrollSpy'

interface HeaderProps {
  theme: 'light' | 'dark'
  toggleTheme: () => void
}

const sectionIds = ['home', 'about', 'community', 'events', 'faq']
const sections = [['about', 'O nás'], ['community', 'Komunita'], ['events', 'Akce'], ['faq', 'FAQ']]

function Header({ theme, toggleTheme }: HeaderProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const activeSection = useScrollSpy(sectionIds)
  const account = useRef<HTMLDetailsElement>(null)

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !account.current?.contains(event.target)) {
        account.current?.removeAttribute('open')
      }
    }
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && account.current?.open) {
        account.current.removeAttribute('open')
        account.current.querySelector('summary')?.focus()
      }
    }
    document.addEventListener('pointerdown', closeOutside)
    document.addEventListener('keydown', closeWithEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOutside)
      document.removeEventListener('keydown', closeWithEscape)
    }
  }, [])

  const closeAccount = () => account.current?.removeAttribute('open')

  return (
    <header className="ac-header">
      <a href="#main-content" className="ac-skip">Přejít na obsah</a>
      <div className="ac-header-inner ac-width">
        <Link to="/" className="ac-logo" aria-label="Allcom — domů">
          <img src="/favicon.svg" alt="" width="40" height="40" />
          <span>Allcom<span className="ac-logo-zone">.zone</span></span>
        </Link>
        <nav className="ac-nav" aria-label="Hlavní navigace">
          {sections.map(([id, label]) => (
            <a key={id} href={`/#${id}`} aria-current={location.pathname === '/' && activeSection === id ? 'location' : undefined}>{label}</a>
          ))}
          <Link to="/shop" aria-current={location.pathname === '/shop' ? 'page' : undefined}>Shop</Link>
          <Link to="/discord" aria-current={location.pathname.startsWith('/discord') ? 'page' : undefined}>Discord docs</Link>
        </nav>
        <div className="ac-account-actions">
          <button type="button" className="ac-theme" onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Přepnout na světlé téma' : 'Přepnout na tmavé téma'}>
            {theme === 'dark' ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path d="M20.5 14A9 9 0 0 1 10 3.5 9 9 0 1 0 20.5 14Z" />
              </svg>
            )}
          </button>
          {user ? (
            <details ref={account} className="ac-user-menu">
              <summary aria-label="Uživatelský účet">{(user.username || user.email || 'U')[0].toUpperCase()}</summary>
              <div className="ac-user-panel">
                <strong>{user.username || user.email || 'Uživatel'}</strong>
                <span>{(user.economyStats?.coinsCount || 0).toLocaleString('cs-CZ')} mincí</span>
                <Link to="/profile" onClick={closeAccount}>Profil</Link>
                <Link to="/shop" onClick={closeAccount}>Shop</Link>
                <a href="https://disboard.org/review/create/1325522814895263775" onClick={closeAccount} target="_blank" rel="noopener noreferrer">Napsat recenzi na DISBOARDu</a>
                <button type="button" onClick={async () => { try { await logout() } finally { closeAccount() } }}>Odhlásit se</button>
              </div>
            </details>
          ) : <Link to="/login" className="ac-login">Přihlásit se</Link>}
        </div>
      </div>
    </header>
  )
}

export default Header
